import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Box, Button, TextField, Alert, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { swarmApi } from '../api';

function SecretsAdmin() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState({ open: false, name: '', value: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await swarmApi.listSecrets();
      setItems(res.data || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load secrets');
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!dialog.name.trim() || !dialog.value) {
      setError('Name and value required');
      return;
    }
    setSaving(true);
    try {
      await swarmApi.createSecret({
        name: dialog.name.trim(),
        value: dialog.value,
        description: dialog.description || null,
      });
      setDialog({ open: false, name: '', value: '', description: '' });
      setSuccess('Secret stored (value will not be shown again). Reference as secret:name in workload env.');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete secret "${name}"?`)) return;
    try {
      await swarmApi.deleteSecret(id);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Secrets</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialog({ open: true, name: '', value: '', description: '' })}>Add secret</Button>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Values are encrypted at rest and never returned by the API. In a workload env var use <code>KEY=secret:name</code>.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id}>
                <TableCell><code>{s.name}</code></TableCell>
                <TableCell>{s.companyId ?? 'global'}</TableCell>
                <TableCell>{s.description || '—'}</TableCell>
                <TableCell>{s.updatedAt ? new Date(s.updatedAt).toLocaleString() : '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => remove(s.id, s.name)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No secrets yet</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={() => setDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>New secret</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Name" value={dialog.name}
            onChange={(e) => setDialog(d => ({ ...d, name: e.target.value }))}
            helperText="e.g. db_password — reference as secret:db_password" />
          <TextField fullWidth margin="normal" label="Value" type="password" value={dialog.value}
            onChange={(e) => setDialog(d => ({ ...d, value: e.target.value }))} />
          <TextField fullWidth margin="normal" label="Description" value={dialog.description}
            onChange={(e) => setDialog(d => ({ ...d, description: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={saving}>{saving ? 'Saving…' : 'Store'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SecretsAdmin;
